import { createHash } from "node:crypto";
import {
  getSourceKey,
  type PluginRunContext,
  type PluginRunTranscriptEntry,
  type PluginTaskContext,
} from "@sentry/junior-plugin-api";
import { z } from "zod";
import {
  createMemoryStore,
  type CreateMemoryInput,
  type CreateMemoryResult,
  type MemoryDb,
} from "./store";
import {
  createMemoryAgent,
  parseExtractedMemory,
  type ExtractedMemory,
  type MemoryExtractionResult,
} from "./agent";
import { MEMORY_KINDS, memoryRuntimeContextSchema } from "./types";
import { capturedMemory, memoriesCapturedEvent } from "./events";

const MEMORY_TOOL_NAMES = new Set([
  "archiveMemory",
  "createMemory",
  "listMemories",
  "searchMemories",
]);
const MEMORY_TASK_STATE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const extractedMemorySchema = z
  .object({
    content: z.string().min(1),
    expiresAtMs: z.number().finite().nullable(),
    kind: z.enum(MEMORY_KINDS),
    evidenceMessageIndices: z
      .array(z.number().int().nonnegative())
      .min(1)
      .max(10),
  })
  .strict()
  .transform(parseExtractedMemory);
const extractedMemoryCacheSchema = z.union([
  z
    .object({
      costUsd: z.number().finite().nonnegative().optional(),
      memories: z.array(extractedMemorySchema).max(5),
    })
    .strict(),
  z
    .array(extractedMemorySchema)
    .max(5)
    .transform((memories) => ({ memories })),
]);

/** Subject for a passively extracted memory, or drop when unproven. */
type MemorySubjectTarget = "drop" | "user" | "conversation";

function recordCapturedMemory(
  captured: ReturnType<typeof capturedMemory>[],
  result: CreateMemoryResult,
): void {
  const supersededIds = new Set(result.supersededIds ?? []);
  for (let index = captured.length - 1; index >= 0; index -= 1) {
    if (supersededIds.has(captured[index]!.id)) {
      captured.splice(index, 1);
    }
  }
  if (result.created || result.idempotent) {
    captured.push(capturedMemory(result.memory));
  }
}

/** A cited entry is a run-actor durable instruction evidence entry. */
function isRunActorInstruction(entry: PluginRunTranscriptEntry): boolean {
  return (
    entry.type === "message" &&
    entry.role === "user" &&
    entry.provenance?.authority === "instruction" &&
    entry.isRunActor === true
  );
}

/** A cited entry is valid public conversation evidence for shared knowledge. */
function isConversationEvidence(entry: PluginRunTranscriptEntry): boolean {
  if (entry.type === "toolResult") {
    return entry.isError === false && Boolean(entry.text?.trim());
  }
  if (
    entry.type === "message" &&
    entry.role === "user" &&
    entry.provenance?.authority === "instruction" &&
    entry.isRunActor === false
  ) {
    return Boolean(entry.provenance.actor);
  }
  return (
    entry.type === "message" &&
    entry.role === "user" &&
    entry.provenance?.authority === "context"
  );
}

/** Resolve the deduplicated cited transcript entries, failing on bad indices. */
function citedEntries(
  indices: number[],
  transcript: PluginRunTranscriptEntry[],
): { valid: boolean; entries: PluginRunTranscriptEntry[] } {
  const seen = new Set<number>();
  const entries: PluginRunTranscriptEntry[] = [];
  for (const index of indices) {
    if (seen.has(index)) {
      continue;
    }
    seen.add(index);
    const entry = transcript[index];
    if (!entry) {
      return { valid: false, entries: [] };
    }
    entries.push(entry);
  }
  return { valid: entries.length > 0, entries };
}

/**
 * Verify an extracted memory against runtime-owned provenance on its cited
 * evidence. This is a deterministic authority boundary, not a model decision:
 * personal preferences require a single-actor run whose citations are all
 * run-actor instructions, conversation knowledge requires run-actor instruction
 * or valid public conversation evidence, and anything unproven (including
 * missing provenance) is dropped. Multi-actor runs interleave first-person
 * statements from different people, so they never store a preference regardless
 * of citations; a personal preference can wait for a single-actor run.
 */
function routeExtractedMemory(
  memory: ExtractedMemory,
  transcript: PluginRunTranscriptEntry[],
  run: Pick<PluginRunContext, "actor" | "actors" | "actorUserId">,
): MemorySubjectTarget {
  const cited = citedEntries(memory.evidenceMessageIndices, transcript);
  if (!cited.valid) {
    return "drop";
  }
  if (memory.kind === "preference") {
    // Only a run attributed to exactly one human run actor may store a preference.
    const exactlyOneHumanRunActor =
      run.actorUserId !== undefined &&
      run.actor !== undefined &&
      run.actor.platform !== "system" &&
      run.actors.length === 1 &&
      run.actors[0]?.platform !== "system";
    if (!exactlyOneHumanRunActor) {
      return "drop";
    }
    // Never downgrade an unproven first-person preference to conversation subject.
    return cited.entries.every(isRunActorInstruction) ? "user" : "drop";
  }
  return cited.entries.every(
    (entry) => isRunActorInstruction(entry) || isConversationEvidence(entry),
  )
    ? "conversation"
    : "drop";
}

function memoryIdempotencySuffix(
  memory: ExtractedMemory,
  target: MemorySubjectTarget,
): string {
  return createHash("sha256")
    .update(target)
    .update("\0")
    .update(memory.kind)
    .update("\0")
    .update(memory.content)
    .update("\0")
    .update(memory.expiresAtMs === null ? "never" : String(memory.expiresAtMs))
    .digest("hex")
    .slice(0, 32);
}

function passiveInput(
  sessionId: string,
  memory: ExtractedMemory,
  sourceKey: string,
  target: MemorySubjectTarget,
): CreateMemoryInput {
  return {
    content: memory.content,
    idempotencyKey: `session:${sourceKey}:${sessionId}:${memoryIdempotencySuffix(memory, target)}`,
    kind: memory.kind,
    ...(memory.expiresAtMs !== null
      ? { expiresAtMs: memory.expiresAtMs }
      : undefined),
  };
}

async function getTaskExtraction(
  context: PluginTaskContext,
  extract: () => Promise<MemoryExtractionResult>,
): Promise<MemoryExtractionResult> {
  const cacheKey = `memory-extraction:${context.id}`;
  const cached = await context.state.get(cacheKey);
  if (cached !== undefined) {
    const parsed = extractedMemoryCacheSchema.safeParse(cached);
    if (parsed.success) {
      return parsed.data;
    }
    await context.state.delete(cacheKey);
  }
  const extraction = await extract();
  await context.state.set(cacheKey, extraction, MEMORY_TASK_STATE_TTL_MS);
  return extraction;
}

/**
 * Extract and store memories from a completed session plugin task.
 *
 * Memory owns learning after a run and reads only the plugin run data.
 * Explicit memory tools stay separate so retries do not reinterpret user
 * requests.
 */
export async function processMemorySession(
  context: PluginTaskContext,
): Promise<void> {
  const run = await context.run.load();
  // TODO(dcramer): Replace this hardcoded Source check when a plugin Run can
  // state whether passive memory extraction should run.
  if (run.source.kind !== "slack" && run.source.kind !== "web") {
    return;
  }
  // Memory tool turns already own memory management or recall; do not reinterpret
  // recalled memory output as fresh passive-learning evidence.
  if (
    run.transcript.some(
      (entry) =>
        entry.type === "toolResult" && MEMORY_TOOL_NAMES.has(entry.toolName),
    )
  ) {
    return;
  }
  const sourceKey = getSourceKey(run.source);
  if (!sourceKey || (run.source.visibility === "private" && !run.actorUserId)) {
    return;
  }
  const transcript = run.transcript
    .filter((entry) => entry.text?.trim())
    .map((entry) => ({ ...entry, text: entry.text!.trim() }));
  const evidenceText = transcript
    .filter((entry) => entry.type === "toolResult" || entry.role === "user")
    .map((entry) => entry.text)
    .join("\n\n")
    .trim();
  if (!evidenceText) {
    return;
  }

  const runtimeContext = memoryRuntimeContextSchema.parse({
    conversationId: run.conversationId,
    ...(run.locationId ? { locationId: run.locationId } : undefined),
    ...(run.actor ? { actor: run.actor } : undefined),
    source: run.source,
    ...(run.actorUserId ? { userId: run.actorUserId } : undefined),
  });
  const agent = createMemoryAgent(context.model);
  const store = createMemoryStore(context.db as MemoryDb, runtimeContext, {
    embedder: context.embedder,
    supersessionDecider: agent,
  });
  await store.archiveExpiredMemories();
  const extraction = await getTaskExtraction(context, async () => {
    const existingMemories = await store.searchMemories({
      limit: 10,
      query: evidenceText,
    });
    return await agent.extractSessionMemories({
      existingMemories: existingMemories.map((memory) => ({
        content: memory.content,
      })),
      actors: run.actors,
      transcript,
      runtimeContext,
    });
  });

  const captured: ReturnType<typeof capturedMemory>[] = [];
  for (const memory of extraction.memories) {
    // The routing gate stays even though extraction is also actor-gated:
    // getTaskExtraction caches extraction output for 7 days, so a retry can replay
    // preference proposals cached before this gate existed.
    const target = routeExtractedMemory(memory, transcript, run);
    if (target === "drop") {
      continue;
    }
    const input = passiveInput(run.runId, memory, sourceKey, target);
    if (target === "conversation") {
      const result = await store.createConversationMemory(input);
      recordCapturedMemory(captured, result);
      continue;
    }
    const result = await store.createMemory(input);
    recordCapturedMemory(captured, result);
  }
  await context.events.emit(
    memoriesCapturedEvent({
      memories: captured,
      ...(extraction.costUsd !== undefined
        ? { costUsd: extraction.costUsd }
        : undefined),
    }),
  );
}
